import { Handle, Position } from '@xyflow/react'
import type { Node, NodeProps } from '@xyflow/react'
import type { CSSProperties } from 'react'
import { Lamp } from '../../../components/Lamp'
import type { NetworkNode, NetworkSide } from '../../../types'
import type { LiveDevice } from './netModel'
import { convLine, kindInfo, lampOf } from './netModel'

// 도면 노드 — 종류 표시 · 이름 · IP, IP 문제가 있으면 붉은 테두리. 이 IP 로 통신하는 Edge 디바이스는 ● 코드 칩 (램프 = 통신 상태).
// 연결점은 위·아래·왼쪽·오른쪽·네 모서리 8개 (마우스를 올리거나 고르면 보인다) — 느슨한 연결 모드라 어느 점끼리도 이을 수 있다

export type NetNodeData = {
  node: NetworkNode
  name: string
  ip: string
  live?: LiveDevice
  missing: boolean
  issue: string | null
  editors: { name: string; color: string }[] // 이 노드를 잡고 있는 다른 사람
  comms: LiveDevice[] // 이 장비 IP 로 통신하는 Edge 디바이스
}

export type NetFlowNode = Node<NetNodeData, 'net'>

// 모서리 점은 위·아래 변의 양 끝 — 선은 위·아래로 나간다
const HANDLES: { id: NetworkSide; position: Position; style?: CSSProperties }[] = [
  { id: 't', position: Position.Top },
  { id: 'b', position: Position.Bottom },
  { id: 'l', position: Position.Left },
  { id: 'r', position: Position.Right },
  { id: 'tl', position: Position.Top, style: { left: 0 } },
  { id: 'tr', position: Position.Top, style: { left: '100%' } },
  { id: 'bl', position: Position.Bottom, style: { left: 0 } },
  { id: 'br', position: Position.Bottom, style: { left: '100%' } },
]

const KIND_TONE: Record<string, string> = {
  edge: 'bg-accent text-white',
  switch: 'bg-gray-700 text-white',
  hub: 'bg-gray-500 text-white',
  router: 'bg-gray-700 text-white',
  converter: 'bg-amber-600 text-white',
  pc: 'bg-gray-400 text-white',
  equipment: 'bg-emerald-700 text-white',
  device: 'bg-surface text-gray-600 border border-gray-300',
  other: 'bg-gray-200 text-gray-600',
}

export default function NetNode({ data, selected }: NodeProps<NetFlowNode>) {
  const { node, name, ip, live, missing, issue, editors, comms } = data
  const info = kindInfo(node.kind)
  const lamp = live ? lampOf(live) : null
  const handles = HANDLES.map(h => (
    <Handle key={h.id} id={h.id} type="source" position={h.position} style={h.style} className="!w-3 !h-3 !bg-gray-400 !border-surface" />
  ))
  const tags = editors.length > 0 && (
    <div className="absolute -top-5 left-0 flex gap-1 pointer-events-none">
      {editors.map(e => <span key={e.name} className="px-1.5 rounded text-[11px] text-white whitespace-nowrap" style={{ background: e.color }}>{e.name}</span>)}
    </div>
  )

  return (
    <div title={issue ?? undefined} style={editors.length && !selected ? { boxShadow: `0 0 0 2px ${editors[0].color}` } : undefined}
      className={`net-node relative min-w-[180px] max-w-[260px] rounded-lg border bg-surface shadow-sm px-3 py-2.5 text-sm
        ${selected ? 'border-accent ring-2 ring-accent/30' : issue ? 'border-red-400' : 'border-gray-300'}
        ${missing ? 'opacity-60 border-dashed' : ''}`}>
      {handles}
      {tags}
      <div className="flex items-center gap-1.5">
        <span className={`shrink-0 px-1.5 py-px rounded text-[11px] font-semibold tracking-wide ${KIND_TONE[node.kind] ?? KIND_TONE.other}`}>
          {info.short}{node.ports > 0 ? ` ${node.ports}p` : ''}
        </span>
        <span className="font-medium text-gray-800 truncate">{name || <span className="text-gray-400 font-normal">{info.label}</span>}</span>
        {lamp && <Lamp state={lamp} className="ml-auto shrink-0" />}
      </div>
      <div className="mt-1 font-mono text-xs">
        <span className={issue ? 'text-red-600' : 'text-gray-600'}>{ip || <span className="text-gray-300">IP 없음</span>}</span>
      </div>
      {convLine(node) && <div className="mt-0.5 text-[11px] font-medium text-amber-700">{convLine(node)}</div>}
      {comms.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {comms.slice(0, 8).map(d => (
            <span key={d.code} title={`${d.name} · ${d.protocol}${d.port ? ` · 포트 ${d.port}` : ''}`}
              className="inline-flex items-center gap-1 px-1.5 rounded bg-blue-50 text-[11px] text-blue-700 font-mono">
              <Lamp state={lampOf(d)} />{d.code}{d.port ? <span className="text-blue-400">:{d.port}</span> : null}
            </span>
          ))}
          {comms.length > 8 && <span className="text-[11px] text-gray-400">+{comms.length - 8}</span>}
        </div>
      )}
      {(node.model || node.location) && (
        <div className="mt-0.5 text-[11px] text-gray-400 truncate">{[node.model, node.location].filter(Boolean).join(' · ')}</div>
      )}
    </div>
  )
}
